/**
 * App config for Supplier Gateway.
 *
 * AWS values (Cognito/S3 IDs) are NOT compiled into the bundle. They are
 * fetched at runtime from `/config.json`, which the CDK writes per stage from
 * the deployed stack outputs. This lets the SPA build once and run against any
 * stage (beta/gamma/prod). Call `loadRuntimeConfig()` once at startup before
 * rendering, then read values via `getAwsConfig()`.
 */

export interface AwsRuntimeConfig {
    region: string;
    userPoolId: string;
    userPoolClientId: string;
    identityPoolId: string;
    uploadsBucket: string;
    /** ASN HTTP API base URL for the integration guide. */
    asnApiEndpoint?: string;
    /** OAuth2 token endpoint for M2M client credentials. */
    oauthTokenEndpoint?: string;
    /** Cognito Hosted UI base URL for employee SSO (set only when SSO is on). */
    hostedUiDomain?: string;
    /** Employee SSO Hosted-UI client id (set only when SSO is on). */
    ssoClientId?: string;
    /** True when supplier IdPrism login (federated via Cognito) is available. */
    idprismSsoEnabled?: boolean;
    stage?: string;
}

let awsConfig: AwsRuntimeConfig | null = null;

/**
 * Fetch `/config.json` and cache it. Safe to call multiple times; only the
 * first successful load takes effect. Throws if the file is missing or invalid,
 * since the app cannot talk to AWS without it.
 */
export async function loadRuntimeConfig(): Promise<AwsRuntimeConfig> {
    if (awsConfig) {
        return awsConfig;
    }
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (!res.ok) {
        throw new Error(`Failed to load runtime config: ${res.status} ${res.statusText}`);
    }
    awsConfig = (await res.json()) as AwsRuntimeConfig;
    return awsConfig;
}

/**
 * Return the loaded AWS runtime config. Throws if `loadRuntimeConfig()` has not
 * completed yet — call it during app startup.
 */
export function getAwsConfig(): AwsRuntimeConfig {
    if (!awsConfig) {
        throw new Error('Runtime config not loaded. Call loadRuntimeConfig() at startup.');
    }
    return awsConfig;
}

/** True when the deployed stage has employee SSO (Midway) configured. */
export function isSsoEnabled(): boolean {
    return Boolean(awsConfig?.hostedUiDomain && awsConfig?.ssoClientId);
}

/** True when supplier IdPrism login (federated via Cognito) is available. */
export function isIdPrismSsoEnabled(): boolean {
    return Boolean(awsConfig?.idprismSsoEnabled);
}

/**
 * Personal email domains rejected at sign-up (corporate-email only). Static, so
 * it stays compiled into the bundle. Set to [] to allow any valid email.
 */
export const blockedEmailDomains: readonly string[] = [
    'gmail.com',
    'googlemail.com',
    'outlook.com',
    'hotmail.com',
    'live.com',
    'yahoo.com',
    'icloud.com',
    'aol.com',
    'proton.me',
    'protonmail.com',
];
